import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './ChatMessage.css'; // Create this file for custom styles

// Custom renderer for links in chatbot answers. AWS retired the individual
// case-study pages this bot cites, so the backend swaps dead links for an
// archived (Wayback Machine) snapshot. This renderer flags those links with
// a visible badge and a custom tooltip (rather than relying on the native
// browser title tooltip, which is small, slow to appear, and easy to miss)
// so it reads as an intentional design choice rather than a broken link.
function MarkdownLink({ href, children, ...props }) {
  const isArchived = typeof href === 'string' && href.includes('web.archive.org');

  // Screen readers announce links that open in a new tab differently than
  // ones that navigate in place; since sighted users can already tell from
  // the browser opening a new tab, this text is visually hidden but still
  // read aloud (see .sr-only in index.css).
  const newTabHint = <span className="sr-only"> (opens in new tab)</span>;

  if (!isArchived) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
        {children}
        {newTabHint}
      </a>
    );
  }

  return (
    <span className="archived-link-wrap">
      <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
        {children}
        {' '}
        <span className="archived-badge">archived</span>
        {newTabHint}
      </a>
      <span className="archived-tooltip" role="tooltip">
        The original page has moved or been removed, so this links to an archived snapshot via the Internet Archive's Wayback Machine.
      </span>
    </span>
  );
}

// Renders chat messages
function ChatMessage({ message }) {
  return (
    <div className={`chat-message ${message.role === 'user' ? 'user' : 'bot'}`}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ a: MarkdownLink }}>
        {message.content}
      </ReactMarkdown>
    </div>
  );
}

export default ChatMessage;
